<template>
  <span>
    <v-btn
      v-if="!hideActivator"
      :color="color"
      tabindex="-1"
      @click="open"
      text
      small
      :class="$vuetify.breakpoint.mdAndDown ? 'mr-n1 ml-n1' : 'pr-0 pl-0'"
    >
      <v-icon small :class="$vuetify.breakpoint.smAndDown ? '' : 'mr-1'">mdi-email-multiple-outline</v-icon>
      <span v-if="!$vuetify.breakpoint.smAndDown">{{ $t("components.template.dialog.selection.templateButton") }}</span>
    </v-btn>
    <confirm-action-dialog
      :fullscreen="true"
      :isDialogActive.sync="isDialogActive"
      :title="$t('components.template.dialog.selection.title')"
      :hideRight="!isHtmlEditorV2Enabled"
      :rightText="$t('components.template.picker.use')"
      :rightDisabled="!previewedTemplate"
      :supressKeyboardActions="true"
      :selectedItems.sync="selectedItems"
      @leftClick="isDialogActive = false"
      @rightClick="usePreviewed"
    >
      <div :style="`height: calc(100vh - 190px); overflow: ${isHtmlEditorV2Enabled ? 'hidden' : 'auto'}`">
        <template-picker
          v-if="isHtmlEditorV2Enabled"
          :partnerId="partnerId"
          :context="context"
          :language="language"
          :isPlain="isPlain"
          :allowHtml="allowHtml"
          @preview="previewedTemplate = $event"
          @select="onSelect"
        />
        <template-table
          v-else
          :partnerId="partnerId"
          :context="context"
          :controlElements="[]"
          :filters="filters"
          :isOpenSideCard="false"
          @click:row="$emit('select', $event)"
        >
          <template #bodyActions>
            <v-btn elevation="0" small color="primary" @click="goToTemplates">
              {{ $t("nav.PARTNER_TEMPLATE_TABLE.title") }}
            </v-btn>
          </template>
        </template-table>
      </div>
    </confirm-action-dialog>
  </span>
</template>

<script lang="ts">
import { TemplateTokenConfig } from "@/lib/configuration/template-token.configuration";
import { TokenCategoriesEnum } from "@/lib/enum/templateEnums/tokenCategories.enum";
import { ITemplateContext } from "@/lib/interfaces/template/templateContext.interface";
import { PartnerTemplateModule } from "@/store/modules/partner-template.store";
import { Component, Prop, Vue } from "vue-property-decorator";
import {
  MrfiktivAdminTemplateViewModelGen,
  MrfiktivPartnerTemplateViewModelGen
} from "../../services/mrfiktiv/v1/data-contracts";
import { AdminTemplateModule } from "../../store/modules/admin-template.store";
import Card from "../utility/Card.vue";
import ConfirmActionDialog from "../utility/ConfirmActionDialog.vue";
import TableWrapper, { ITableWrapperHeader } from "../utility/TableWrapper.vue";
import { getFlagEmojiByLanguage } from "@/lib/CountryCodeHelper";
import { IPageFilterElement, PageFilterElement } from "@/models/page-filter-element.entity";
import { PageFilterOperationEnum } from "@/lib/utility/data/page-filter-operation.enum";
import { MessageContentFormatEnum } from "@/lib/enum/templateEnums/messageContentFormat.enum";
import TemplateTable from "./TemplateTable.vue";
import TemplatePicker from "./TemplatePicker.vue";
import { ITemplate } from "@/models/template.entity";
import { PartnerTemplateGoToHelper } from "@/lib/utility/partner-template.go-to-helper";
import { AdminTemplateGoToHelper } from "@/lib/utility/admin-template.go-to-helper";
import { FeatureModule } from "@/store/modules/feature.store";
import { PartnerModule } from "@/store/modules/partner";
import { resolveTemplateLanguage } from "@/lib/utility/template-language.helper";
import { LanguageCodeEnum } from "@/lib/enum/language-code.enum";

@Component({
  components: { ConfirmActionDialog, Card, TableWrapper, TemplateTable, TemplatePicker },
  filters: {
    getFlagEmojiByLanguage
  }
})
export default class TemplateSelectionDialog extends Vue {
  @Prop()
  partnerId?: string;

  @Prop()
  context!: ITemplateContext;

  @Prop()
  isPlain?: boolean;

  @Prop({ default: true })
  allowHtml?: boolean;

  @Prop({ default: "" })
  color!: string;

  @Prop({ default: false })
  hideActivator!: boolean;

  isDialogActive = false;

  selectedItems: ITemplate[] = [];

  loading = false;

  previewedTemplate: ITemplate | null = null;

  get filters() {
    if (!this.isPlain) {
      return [];
    }

    const filters: IPageFilterElement[] = [
      new PageFilterElement({
        key: "meta.contentFormat",
        operation: PageFilterOperationEnum.EQUAL,
        value: MessageContentFormatEnum.PLAIN
      })
    ];

    if (this.partnerId) {
      filters.push(
        new PageFilterElement({
          key: "isAdminTemplate",
          operation: PageFilterOperationEnum.EQUAL,
          value: false as any
        })
      );
    }

    return filters;
  }

  get headers(): ITableWrapperHeader[] {
    const headers: ITableWrapperHeader[] = [
      { text: String(this.$t("components.template.dialog.selection.tempalteTitle")), value: "meta.title" },
      { text: String(this.$t("language")), value: "meta.language" },
      {
        text: String(this.$t("components.template.dialog.selection.description")),
        value: "meta.description",
        sortable: false
      },
      {
        text: String(this.$t("components.template.dialog.selection.categories")),
        value: "meta.categories",
        sortable: false
      },
      {
        text: String(this.$t("components.template.dialog.selection.context")),
        value: "context",
        sortable: false
      }
    ];

    if (this.partnerId) {
      headers.push({
        text: String(this.$t("components.template.dialog.selection.isAdminTemplate")),
        value: "isAdminTemplate",
        align: "center",
        width: "30px",
        sortable: false
      });
    }

    headers.push({
      text: "",
      value: "timestamp.created",
      type: "date",
      width: "40px",
      align: "end"
    });

    return headers;
  }

  get templates(): MrfiktivPartnerTemplateViewModelGen[] | MrfiktivAdminTemplateViewModelGen[] {
    let templates: MrfiktivPartnerTemplateViewModelGen[] | MrfiktivAdminTemplateViewModelGen[] = [];
    if (this.partnerId) {
      templates = PartnerTemplateModule.filteredAndSorted;
    } else {
      templates = AdminTemplateModule.filteredAndSorted;
    }

    if (!templates) {
      return [];
    }

    return templates;
  }

  icon(item: any) {
    return item.isAdminTemplate ? "mdi-check" : "mdi-close";
  }

  getDescription(item: MrfiktivPartnerTemplateViewModelGen | MrfiktivAdminTemplateViewModelGen) {
    const text = item.meta.description || item.content.subject || item.content.body || "";
    if (text.length > 200) {
      return text.substring(0, 200) + "...";
    }

    return text;
  }

  getTemplateContext(template: MrfiktivPartnerTemplateViewModelGen | MrfiktivAdminTemplateViewModelGen) {
    const available: string[] = [];
    const missing: string[] = [];
    const tokens = template.content?.tokens;

    if (!tokens) {
      return { available, missing };
    }

    const tokenCategories = tokens
      .map((token) => token.split(".")[0])
      .filter((cat: string) => TemplateTokenConfig.objectCategories.includes(cat as TokenCategoriesEnum));

    tokenCategories.forEach((category) => {
      if (!this.context[category]) {
        missing.push(category);
      } else {
        available.push(category);
      }
    });

    return { available: [...new Set(available)], missing: [...new Set(missing)] };
  }

  get isHtmlEditorV2Enabled() {
    return FeatureModule.isHtmlEditorV2Enabled;
  }

  get language(): LanguageCodeEnum {
    return resolveTemplateLanguage(
      this.context,
      PartnerModule.partner?.language,
      this.$i18n.locale as LanguageCodeEnum
    );
  }

  onSelect(template: ITemplate) {
    this.$emit("select", template);
    this.isDialogActive = false;
  }

  usePreviewed() {
    if (this.previewedTemplate) this.onSelect(this.previewedTemplate);
  }

  open() {
    this.previewedTemplate = null;
    this.isDialogActive = true;
  }

  goToTemplates() {
    if (this.partnerId) {
      new PartnerTemplateGoToHelper(this.$router).goToPartnerTemplateTable({ partnerId: this.partnerId });
    } else {
      new AdminTemplateGoToHelper(this.$router).goToAdminTemplateTable({});
    }
  }
}
</script>
